import { isNewIdentifiant, request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import { LoadingContext, PayeurContext, RegieContext } from '@/contexts';
import type { PayeurContextType } from '@/contexts/PayeurContext';
import { getPayeurFactures } from '@/routes';
import type { ApiResponseType } from '@/types';
import type { PayeurFacture } from '@/types/payeur';

type PayeurContextProviderProps = {
  identifiantPayeur?: number | string;
  children: React.ReactNode;
};

const PayeurContextProvider = ({ identifiantPayeur, children }: PayeurContextProviderProps) => {
  const [factures, setFactures] = React.useState<PayeurFacture[]>();

  const { setPayeurLoadings } = React.useContext(LoadingContext);
  const { selectedRegie } = React.useContext(RegieContext);

  const loadFactures = React.useCallback(
    (silent = false) => {
      const pendingPromises = [];

      if (!silent) {
        setFactures([]);
        setPayeurLoadings((prev) => ({ ...prev, factures: true }));
      }

      request
        .get(getPayeurFactures({ identifiantPayeur }))
        .then(({ data, meta: { lastPage } }: ApiResponseType<PayeurFacture>) => {
          for (let i = 2; i <= lastPage; i += 1) {
            pendingPromises.push(request.get(getPayeurFactures({ identifiantPayeur, page: i })));
          }

          Promise.all(pendingPromises)
            .then((datas) => {
              let dataFormatted: PayeurFacture[] = data;

              _.forEach(datas, ({ data: d }) => {
                dataFormatted = [...dataFormatted, ...d];
              });

              setFactures(dataFormatted);
            })
            .finally(() => {
              setPayeurLoadings((prev) => ({ ...prev, factures: false }));
            });
        });
    },
    [identifiantPayeur, setPayeurLoadings],
  );

  React.useEffect(() => {
    if (selectedRegie) {
      if (_.isNil(identifiantPayeur) || isNewIdentifiant(identifiantPayeur)) {
        setFactures(null);
      } else {
        loadFactures();
      }
    }
  }, [selectedRegie, identifiantPayeur, loadFactures]);

  const context = React.useMemo<PayeurContextType>(
    () => ({
      factures,
      getFactures: loadFactures,
    }),
    [factures, loadFactures],
  );

  return <PayeurContext.Provider value={context}>{children}</PayeurContext.Provider>;
};

export default PayeurContextProvider;
